import type React from "react";
import { approvedView, pendingChangeText, type SoaApprovalEntry } from "../lib/soaApproval";
import { useState } from "react";
import { ClipboardList } from "lucide-react";
import { theme, emptyFrame, type as text } from "../theme";
import { useControlTagData, useTagFilter, matchesTagFilter } from "./ControlTags";
import { api } from "../api/client";
import { frameworkAccent } from "./controlStatus";
import { ControlInsightsPanel, insightsKey } from "./ControlInsightsPanel";
import type { ControlInsights } from "./ControlInsightsPanel";
import { AiRecommendations } from "./AiRecommendations";
import { displayControlRef, displayControlTitle } from "../lib/controlRef";
import { ControlGroup, ControlGroupStack, ControlListFrame, pluralNoun } from "./standards/ControlGroup";
import { ControlRow } from "./standards/ControlRow";
import { ControlDetail, evidenceCountFor, soaStatusOptions, type EvidenceDoc as StandardEvidenceDoc } from "./standards/ControlDetail";
import type { RecordCheckResult } from "./RecordChecks";
import { ControlFilterBar, statusFilterOptions, matchesControlSearch, isGroupOpen, toggleGroup, type GroupExpansion } from "./standards/ControlFilterBar";
import { StatusSummary, countStatuses } from "./standards/StatusSummary";
import { GuidedIntro } from "./standards/StandardHeader";
import { useStandardPermissions } from "./standards/useStandardPermissions";

// The assessment list every SoA-backed standard page renders: ISO 27001 clauses and Annex A,
// ISO 9001, ISO 42001 clauses and Annex B, ISO 45001, ISO 14001, SOC 2, PCI DSS, IASME Cyber
// Assurance and the NCSC CAF. Summary, filter bar, collapsible groups, one row per control and
// the shared detail beneath it (components/standards/*). Pages add their own header and any
// standard-specific context above it; nothing here scores or decides applicability.

export interface SoaRow {
  control: {
    id: string;
    frameworkId: string;
    kind: "CLAUSE" | "CONTROL";
    domain: string | null;
    name: string;
    description: string;
    requiredEvidence: string[];
    /** The page's own guidance for this control (a key audit question, the CAF principle). */
    guidance?: string;
  };
  entry: SoaApprovalEntry & { applicable: boolean | null; justification: string | null; approvalStatus: string; aiNote?: string | null };
  /** Record checks on this control, from GET /controls/soa (absent when none bear on it). */
  recordChecks?: RecordCheckResult[];
}

export type EvidenceDoc = StandardEvidenceDoc;

/** A group the page defines when Control.domain does not carry one (ISO clause numbers, PCI goals). */
export interface ControlGroupDef {
  key: string;
  title: string;
  hint?: string;
  match: (row: SoaRow) => boolean;
}

interface Props {
  rows: SoaRow[];
  docs: EvidenceDoc[];
  frameworkId: string;
  loadAll: () => unknown;
  novice: boolean;
  insightsMap?: Map<string, ControlInsights>;
  /** Groups in display order; without them rows group by Control.domain. */
  groups?: ControlGroupDef[];
  /** Singular noun for counts: "control", "clause", "outcome", "criterion", "requirement", "theme". */
  noun?: string;
  /** The summary's label ("Controls compliant"). */
  summaryLabel?: string;
  /** Label of the status field in the detail ("Operating effectiveness" on SOC 2 Type II). */
  statusLabel?: string;
  /** The group filter's "all" choice ("All clauses", "All objectives"). */
  groupLabel?: string;
  /** Guided-mode explanation above the list. */
  guidedIntro?: { title: string; text: string };
  /**
   * IASME Cyber Assurance records a status without deciding applicability (applicable: null on
   * every write); every other standard marks a row applicable once it is assessed.
   */
  statusLeavesApplicabilityOpen?: boolean;
}

/** "Objective A — Managing security risk · A1 Governance" → title "A1 Governance", hint the objective. */
function splitDomain(domain: string): { title: string; hint?: string } {
  const i = domain.lastIndexOf(" · ");
  return i > 0 ? { title: domain.slice(i + 3), hint: domain.slice(0, i) } : { title: domain };
}

export function FrameworkControlList({
  rows, docs, frameworkId, loadAll, novice, insightsMap, groups: groupDefs,
  noun = "control", summaryLabel, statusLabel, groupLabel, guidedIntro, statusLeavesApplicabilityOpen,
}: Props) {
  const accent = frameworkAccent(frameworkId);
  const { canAssess, canAttach } = useStandardPermissions();
  const tagData = useControlTagData();
  const [tagFilter, setTagFilter] = useTagFilter();
  const [statusFilter, setStatusFilter] = useState("all");
  const [groupFilter, setGroupFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState<string | null>(null);
  const [expansion, setExpansion] = useState<GroupExpansion>("all");
  const [saving, setSaving] = useState<string | null>(null);

  async function updateStatus(controlId: string, status: string) {
    setSaving(controlId);
    try {
      await api.put(`/controls/soa/${controlId}`, {
        applicable: statusLeavesApplicabilityOpen || status === "NOT_STARTED" ? null : true,
        status,
      });
      await loadAll();
    } finally { setSaving(null); }
  }

  // Mirror the newest note into the SoA justification for AI insights and reports.
  async function mirrorNote(controlId: string, body: string) {
    try {
      await api.put(`/controls/soa/${controlId}`, { justification: body });
      await loadAll();
    } catch { /* the note itself is saved; the mirror is best-effort */ }
  }

  // Annex controls the SoA excludes leave the list; clauses always apply.
  // Counted on the approved values: a change awaiting the consultant does not move the summary.
  const applicable = rows.filter((r) => !(r.control.kind === "CONTROL" && approvedView(r.entry).applicable === false));

  // Groups: the page's own, else Control.domain in catalogue order.
  const resolvedGroups: Array<{ key: string; title: string; hint?: string; match: (r: SoaRow) => boolean }> = groupDefs
    ?? Array.from(new Set(rows.map((r) => r.control.domain).filter((d): d is string => !!d))).map((d) => ({
      key: d, ...splitDomain(d), match: (r: SoaRow) => r.control.domain === d,
    }));
  const groupOf = (r: SoaRow) => resolvedGroups.find((g) => g.match(r))?.key ?? null;

  const refOf = (r: SoaRow) => displayControlRef(r.control.id);
  const titleOf = (r: SoaRow) => displayControlTitle(r.control.id, r.control.name);

  const visible = applicable.filter((r) =>
    (statusFilter === "all" || r.entry.status === statusFilter)
    && (groupFilter === "all" || groupOf(r) === groupFilter)
    && matchesTagFilter(tagData, r.control.id, tagFilter)
    && matchesControlSearch(search, refOf(r), titleOf(r), r.control.id));

  const grouped = resolvedGroups
    .map((g) => ({ ...g, rows: visible.filter((r) => groupOf(r) === g.key) }))
    .filter((g) => g.rows.length > 0);
  const ungrouped = visible.filter((r) => groupOf(r) === null);
  const searching = search.trim().length > 0;

  function renderRow(row: SoaRow) {
    const isOpen = open === row.control.id;
    const insights = insightsMap?.get(insightsKey(row.control.frameworkId ?? frameworkId, row.control.id)) ?? null;
    const pending = row.entry.approvalStatus === "DRAFT";
    return (
      <ControlRow
        key={row.control.id}
        reference={refOf(row)}
        title={titleOf(row)}
        subtitle={novice && !isOpen ? row.control.description : undefined}
        status={row.entry.status}
        novice={novice}
        pending={pending}
        evidenceCount={evidenceCountFor(docs, frameworkId, row.control.id)}
        hasNotes={!!row.entry.justification}
        open={isOpen}
        onToggle={() => setOpen(isOpen ? null : row.control.id)}
      >
        <ControlDetail
          controlId={row.control.id}
          requirement={row.control.description}
          guidance={row.control.guidance}
          checklist={row.control.requiredEvidence}
          status={row.entry.status}
          novice={novice}
          statusLabel={statusLabel}
          pending={pending}
          pendingText={novice ? pendingChangeText(row.entry) : (row.entry.status === "COMPLIANT" ? "Compliant, pending consultant approval" : pendingChangeText(row.entry))}
          rejectionReason={row.entry.rejectionReason}
          aiNote={row.entry.aiNote}
          recordChecks={row.recordChecks}
          statusOptions={soaStatusOptions(novice)}
          onSetStatus={(s) => updateStatus(row.control.id, s)}
          saving={saving === row.control.id}
          canEdit={canAssess}
          evidence={{ docs, frameworkId, controlRef: row.control.id, reload: loadAll }}
          canAttach={canAttach}
          notes={{ threadId: row.control.id, legacyNote: row.entry.justification, onPost: (body) => { void mirrorNote(row.control.id, body); } }}
          insights={insights
            ? <ControlInsightsPanel insights={insights} controlId={row.control.id} controlName={row.control.name} framework={frameworkId} status={row.entry.status} notes={row.entry.justification ?? undefined} guidance={row.control.requiredEvidence} />
            : <AiRecommendations controlId={row.control.id} controlName={row.control.name} framework={frameworkId} status={row.entry.status} notes={row.entry.justification ?? undefined} guidance={row.control.requiredEvidence} />}
        />
      </ControlRow>
    );
  }

  const nounPlural = pluralNoun(noun);
  const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

  return (
    <>
      <StatusSummary
        label={summaryLabel ?? `${cap(nounPlural)} compliant`}
        counts={countStatuses(applicable.map((r) => approvedView(r.entry).status))}
        novice={novice}
      />

      {novice && (
        <GuidedIntro title={guidedIntro?.title ?? "How to use this page"} icon={<ClipboardList size={18} />}>
          {guidedIntro?.text ?? "Work through each item below. Open an item to read what it asks, record how well you meet it and attach supporting documents as evidence."}
        </GuidedIntro>
      )}

      <ControlFilterBar
        search={search}
        onSearch={setSearch}
        status={statusFilter}
        onStatus={setStatusFilter}
        statusOptions={statusFilterOptions(novice)}
        group={groupFilter}
        onGroup={setGroupFilter}
        groupOptions={resolvedGroups.map((g) => ({ value: g.key, label: g.title }))}
        groupLabel={groupLabel}
        tagFilter={tagFilter}
        onTagFilter={setTagFilter}
        accent={accent}
        count={{ shown: visible.length, total: applicable.length, noun: nounPlural }}
        onExpandAll={resolvedGroups.length > 0 ? () => setExpansion("all") : undefined}
        onCollapseAll={resolvedGroups.length > 0 ? () => setExpansion("none") : undefined}
      />

      {visible.length === 0 ? (
        <div style={{ ...emptyFrame, padding: "40px 20px", ...text.hint }}>
          No {nounPlural} match these filters.
        </div>
      ) : (
        <ControlGroupStack>
          {grouped.map((g) => {
            const expanded = searching || isGroupOpen(expansion, g.key);
            return (
              <ControlGroup
                key={g.key}
                title={g.title}
                hint={g.hint}
                accent={accent}
                count={g.rows.length}
                noun={noun}
                compliant={g.rows.filter((r) => approvedView(r.entry).status === "COMPLIANT").length}
                gap={g.rows.filter((r) => approvedView(r.entry).status === "GAP").length}
                expanded={expanded}
                onToggle={() => setExpansion((prev) => toggleGroup(prev, g.key, resolvedGroups.map((x) => x.key)))}
              >
                {g.rows.map(renderRow)}
              </ControlGroup>
            );
          })}
          {ungrouped.length > 0 && <ControlListFrame>{ungrouped.map(renderRow)}</ControlListFrame>}
        </ControlGroupStack>
      )}
    </>
  );
}

/** Page-level loading and empty states, the same on every standard. */
export function StandardListState({ loading, empty, emptyTitle, children }: {
  loading: boolean;
  empty: boolean;
  emptyTitle: string;
  children: React.ReactNode;
}) {
  if (loading) return <div style={text.hint}>Loading…</div>;
  if (empty) {
    return (
      <div style={{ ...emptyFrame, padding: "24px 20px" }}>
        <div style={{ ...text.itemTitle, marginBottom: 6 }}>{emptyTitle}</div>
        <div style={{ ...text.hint, color: theme.color.subtle }}>{children}</div>
      </div>
    );
  }
  return null;
}
